import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";
//import { hasPermissionDirect } from "utils/hasPermissions";

const initialForm = {
  noof_courses: "",
  fee_amount: "",
  course_type: "T",
};

export default function SupplyExamFee() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  //const canAdd = hasPermissionDirect("Add Supply Exam Fee", "Exams");
  //const canEdit = hasPermissionDirect("Edit", "Exams");
  //const canDelete = hasPermissionDirect("Delete", "Exams");

  const showDenied = () =>
    setSnackbar({
      open: true,
      message: "You do not have permission for this action",
      severity: "error",
    });

  const fetchData = async (nextPage = page, nextPageSize = pageSize, nextSearch = search) => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/supply-examfee/${clientId}/${collegeId}?page=${
          nextPage + 1
        }&pageSize=${nextPageSize}&search=${encodeURIComponent(nextSearch)}`
      );

      if (data.success) {
        setRows(
          (data.data.items || []).map((row) => ({
            id: row.supply_examfee_id,
            ...row,
          }))
        );
        setRowCount(data.data.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load data",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to load data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, clientId, collegeId]);

  const validateForm = () => {
    const nextErrors = {};
    const noofCourses = Number(form.noof_courses);
    const feeAmount = Number(form.fee_amount);

    if (!Number.isInteger(noofCourses) || noofCourses <= 0) {
      nextErrors.noof_courses = "Enter a positive whole number";
    }

    if (Number.isNaN(feeAmount) || feeAmount < 0) {
      nextErrors.fee_amount = "Enter a valid amount";
    }

    if (!["T", "P"].includes(form.course_type)) {
      nextErrors.course_type = "Select T or P";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleOpen = () => {
    setEditing(null);
    setForm(initialForm);
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleEdit = (row) => {
    setEditing(row);
    setForm({
      noof_courses: row.noof_courses || "",
      fee_amount: row.fee_amount || "",
      course_type: row.course_type || "T",
    });
    setErrors({});
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This supply exam fee record will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/supply-examfee/${clientId}/${collegeId}/${row.supply_examfee_id}`,
        { data: { modified_by: user?.user_email || "system" } }
      );
      setSnackbar({
        open: true,
        message: "Supply Exam Fee Deleted successfully",
        severity: "success",
      });
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Delete failed",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      noof_courses: Number(form.noof_courses),
      fee_amount: Number(form.fee_amount),
      course_type: form.course_type,
    };

    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/supply-examfee/${clientId}/${collegeId}/${editing.supply_examfee_id}`,
          {
            ...payload,
            modified_by: user?.user_email || "system",
          }
        );
        setSnackbar({
          open: true,
          message: "Updated successfully",
          severity: "success",
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/supply-examfee/${clientId}/${collegeId}`, {
          ...payload,
          created_by: user?.user_email || "system",
        });
        setSnackbar({
          open: true,
          message: "Created successfully",
          severity: "success",
        });
      }

      setOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "noof_courses",
      headerName: "No. of Courses",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "fee_amount",
      headerName: "Fee Amount",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => Number(params.value || 0).toFixed(2),
    },
    {
      field: "course_type",
      headerName: "Course Type",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => (params.value === "P" ? "P" : "T"),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      minWidth: 120,
      renderCell: (params) => (
        <ActionsMenu
          row={params.row}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            SUPPLY EXAM FEE
          </Typography>
          <TextField
            size="medium"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Supply Exam Fee
          </Button>
        </Toolbar>
        <Box height={400} mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Supply Exam Fee" : "Add Supply Exam Fee"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="No. of Courses"
              type="number"
              value={form.noof_courses}
              onChange={(e) => setForm({ ...form, noof_courses: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
              error={!!errors.noof_courses}
              helperText={errors.noof_courses}
            />
            <TextField
              label="Fee Amount"
              type="number"
              value={form.fee_amount}
              onChange={(e) => setForm({ ...form, fee_amount: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0, step: "0.01" }}
              error={!!errors.fee_amount}
              helperText={errors.fee_amount}
            />
            <TextField
              select
              label="Course Type"
              value={form.course_type}
              onChange={(e) => setForm({ ...form, course_type: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              error={!!errors.course_type}
              helperText={errors.course_type}
            >
              <option value="T">T</option>
              <option value="P">P</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
